import { ComponentProps, MouseEvent } from "react";
import { UUID } from "@bitory-gmbh/nidavellir";
import { toast } from "sonner";
import t from "@/lang/lang";
import { Button } from "@/shared/components/ui/button";
import { parseRTKQueryErrorToSonner } from "@/shared/components/errors/parse-r-t-k-query-error";
import { useCpyCopyMutation } from "../api/cpy";
import { CpyName } from "../types/entity-names";

export type CpyButtonProps = ComponentProps<typeof Button> & {
  destinationEntityName: CpyName;
  destinationId: UUID;
  sourceEntityName: CpyName;
  sourceId: UUID;
  /**
   * options are passed through to the copy operation. The shape is
   * defined by the operation on the backend.
   */
  options?: unknown;
  /** successMessage is toasted once the copy succeeded. */
  successMessage?: string;
};

/**
 * CpyButton triggers a single, fixed copy operation on click. Source and
 * destination are known upfront, so there is nothing to pick and nothing
 * to confirm — use it wherever a copy is a plain action rather than a
 * selection. Use {@link CpySelect} when the user picks the source, or
 * the CpyDialog for anything more involved.
 *
 * Copying overwrites data in the destination without asking. Only use
 * CpyButton where the label makes that obvious to the user.
 *
 * It accepts every {@link Button} prop, so callers own the appearance,
 * the label and the icon. An onClick passed in still runs — this keeps
 * the button composable, e.g. as the child of a `DropdownMenuItem` with
 * `asChild`, which needs its own handler to close the menu.
 */
export function CpyButton({
  destinationEntityName,
  destinationId,
  sourceEntityName,
  sourceId,
  options,
  successMessage = t("Daten kopiert."),
  disabled,
  onClick,
  ...props
}: CpyButtonProps) {
  const [cpy, { isLoading, reset }] = useCpyCopyMutation();

  const doCpy = (e: MouseEvent<HTMLButtonElement>) => {
    onClick?.(e);

    cpy({
      destinationEntityName,
      destinationId,
      sourceEntityName,
      sourceId,
      options,
    })
      .unwrap()
      .then(() => {
        toast.success(successMessage);
      })
      .catch((err) => {
        toast(...parseRTKQueryErrorToSonner(err));
        reset();
      });
  };

  return <Button {...props} disabled={disabled || isLoading} onClick={doCpy} />;
}
